import React, { FC } from "react";
import { Box, Button, Grid, Typography, TextField, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { StepDots } from "../components/StepDots";
import { ProjectSummaryRow } from "../components/ProjectSummaryRow";
import { ProcessChip } from "../components/ProcessChip";
import { OrderProjectValues, PreviewProjectEstimate, OrderProProcess, orderProjectProductSet } from "../helper";
import { getMilestones, addDays } from "../../../components/ProjectTimeline";
import { OrderPricingBreakdown, OrderPricingBreakdownData } from "../../../components/Pricing/OrderPricingBreakdown";

interface ReviewStepProps {
    values: any;
    projects: OrderProjectValues[];
    contentTypes: { id: number; name: string }[];
    sowOptions: { label: string; value: number }[];
    slackChannels: { label: string; value: string }[];
    slackChannelsLoading: boolean;
    isBrandRole: boolean;
    // Internal users (only) manage selection processes + per-project assignment.
    isInternalRole: boolean;
    orderPros: OrderProProcess[];
    onAddOrderPro: () => void;
    onRemoveOrderPro: (key: string) => void;
    onRenameOrderPro: (key: string, name: string) => void;
    // The process key a project is currently assigned to (resolved w/ fallback).
    getProjectProKey: (p: OrderProjectValues) => string;
    // Whether project `p` may move onto process `key` (same products, or the
    // selection it loaded with) — offered in the per-project selection chip.
    canAssignProject: (p: OrderProjectValues, key: string) => boolean;
    onAssignProject: (index: number, key: string) => void;
    // Create a new (same-product) process for a project and assign it — the
    // per-project selector's "New process" option.
    onAddOrderProForProject: (index: number) => void;
    isSubmitting: boolean;
    // False while the order's selection-process list is loading/failed (edit
    // mode) — Submit stays disabled so a save can't flatten the selections.
    processesReady?: boolean;
    mode: "create" | "edit";
    pricingTotal?: string | null;
    pricingBreakdown?: OrderPricingBreakdownData | null;
    pricingSnapshottedAt?: string | null;
    // Estimated per-project prices keyed by project index (from live preview).
    pricingByKey?: Map<number, PreviewProjectEstimate> | null;
    intakeNotes?: string[];
    // What submitting a brief-drafted order will create. Null outside intake.
    intakeCreates?: {
        brandName: string;
        products: Array<{ placeholder_id: number; name: string; pdp_url: string }>;
        // "Supplier → Retailer" when submitting adds that brand link.
        link?: string | null;
    } | null;
    // Reopens the brief intake match step (brand + product choices).
    onEditIntakeMatches?: () => void;
    onBack?: () => void;
    onEditOrderDetails: () => void;
    onEditProject: (index: number) => void;
    onRemoveProject: (index: number) => void;
    onAddProject: () => void;
    onSubmit: () => void;
}

function ReviewField({ label, value }: { label: string; value: string }) {
    return (
        <Box>
            <Typography
                sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: "var(--text-2)",
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    mb: "3px",
                    fontFamily: "CircularStd"
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: 14,
                    color: "var(--text)",
                    fontWeight: 500,
                    fontFamily: "CircularStd",
                    wordBreak: "break-word"
                }}
            >
                {value || "—"}
            </Typography>
        </Box>
    );
}

export const ReviewStep: FC<ReviewStepProps> = ({
    values,
    projects,
    contentTypes,
    sowOptions,
    slackChannels,
    slackChannelsLoading,
    isBrandRole,
    isInternalRole,
    orderPros,
    onAddOrderPro,
    onRemoveOrderPro,
    onRenameOrderPro,
    getProjectProKey,
    canAssignProject,
    onAssignProject,
    onAddOrderProForProject,
    isSubmitting,
    processesReady = true,
    mode,
    pricingTotal,
    pricingBreakdown,
    pricingSnapshottedAt,
    pricingByKey,
    intakeNotes = [],
    intakeCreates = null,
    onEditIntakeMatches,
    onBack,
    onEditOrderDetails,
    onEditProject,
    onRemoveProject,
    onAddProject,
    onSubmit
}) => {
    // Selection processes are scoped by product SET. Which processes a project
    // may move to is decided by the modal (canAssignProject — it knows what
    // each saved selection held when the modal opened); here we only derive a
    // product label per process from its first project.
    const productNameByKey: Record<string, string> = {};
    const projectCountByProKey: Record<string, number> = {};
    orderPros.forEach((op) => {
        projectCountByProKey[op.key] = 0;
    });
    // First product's title + a count of the rest. product_name alone can't
    // be used: projects picked in the modal already list every title in it.
    const productLabel = (p: OrderProjectValues): string => {
        const count = p.product_ids?.length || (p.product_id != null ? 1 : 0);
        const first = p.products?.[0]?.title || p.product_name;
        if (!first) return "";
        return count > 1 && p.products?.[0]?.title ? `${first} +${count - 1}` : first;
    };
    projects.forEach((p) => {
        const key = getProjectProKey(p);
        if (key in projectCountByProKey) projectCountByProKey[key] += 1;
        if (key in projectCountByProKey && !productNameByKey[key] && orderProjectProductSet(p) != null) {
            const label = productLabel(p);
            if (label) productNameByKey[key] = label;
        }
    });
    // Show the per-project process picker whenever an internal user has more
    // than one project — they may want to split same-product projects across
    // processes (e.g. a later shoot date), not just when products differ.
    const showProcessSelector = isInternalRole && projects.length > 1;

    // A project whose product has no compatible selection resolves to "" (see
    // effectiveProKey) — e.g. its only selection was deleted. Flag those so the
    // user picks or creates a same-product selection before saving, instead of
    // hitting the backend "one product per selection" error on submit.
    const isUnassigned = (p: OrderProjectValues): boolean =>
        showProcessSelector && !orderPros.some((o) => o.key === getProjectProKey(p));
    const unassignedCount = projects.filter(isUnassigned).length;

    // "Add Process" is capped at one selection per project — an order can't have
    // more selections than projects. Below the cap the button either houses the
    // next unassigned product or adds an empty selection to split a product into.
    const atProcessCapacity = orderPros.length >= projects.length;
    let addProcessTooltip = "Add another selection — split a product across its projects";
    if (atProcessCapacity)
        addProcessTooltip = "An order can have at most one selection per project";
    else if (unassignedCount > 0)
        addProcessTooltip = "Create a selection for the next product that needs one";

    const brandName =
        values.brand?.name || (intakeCreates?.brandName ? `${intakeCreates.brandName} (new)` : "—");
    const hasIntakeCreates =
        !!intakeCreates &&
        (!!intakeCreates.brandName || intakeCreates.products.length > 0 || !!intakeCreates.link);
    const sowName = (() => {
        if (!values.sow) return "—";
        const match = sowOptions.find((o) => o.value === values.sow || o.value === Number(values.sow));
        return match?.label ?? String(values.sow);
    })();
    const slackDisplay = (() => {
        if (values.create_slack_channel && values.new_slack_channel_name) {
            return `#${values.new_slack_channel_name} (new)`;
        }
        if (!values.slack_channel) return "—";
        // slack_channel holds the Slack channel ID; show the human name when we can resolve it.
        const opt = slackChannels.find((c) => c.value === values.slack_channel);
        if (opt) return `#${opt.label}`;
        // List not loaded yet (edit mode opens on Review before the fetch lands) —
        // show a neutral placeholder rather than flashing the cryptic raw ID.
        if (slackChannelsLoading) return "…";
        return `#${values.slack_channel}`;
    })();
    const n = projects.length;
    const deliveryOffset = getMilestones(1).at(-1)!.daysOffset;
    const projectDates = projects.map((p) => (p.start_date instanceof Date ? p.start_date : new Date()));
    const latestDelivery = new Date(Math.max(...projectDates.map((d) => addDays(d, deliveryOffset).getTime())));
    const estimatedDays = Math.round((latestDelivery.getTime() - Date.now()) / 86400000);

    const submitLabel = isSubmitting
        ? mode === "edit"
            ? "Saving…"
            : "Creating…"
        : mode === "edit"
        ? "Save Changes"
        : isBrandRole
        ? "Submit Request"
        : "Submit Order";

    return (
        <>
            {/* Body */}
            <Box sx={{ flex: 1, overflowY: "auto", p: { xs: "24px 16px 28px", sm: "24px 36px 28px" } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb="6px">
                    <Typography
                        sx={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: "var(--text)",
                            letterSpacing: -0.4,
                            fontFamily: "CircularStd"
                        }}
                    >
                        Review Order
                    </Typography>
                    <StepDots step={6} />
                </Box>
                <Typography sx={{ fontSize: 13.5, color: "var(--text-2)", mb: "20px", fontFamily: "CircularStd" }}>
                    {isBrandRole
                        ? "Review your request below — we'll follow up with a quote once submitted."
                        : "Confirm the details below — we'll kick off production as soon as you submit."}
                </Typography>

                {intakeNotes.length > 0 && (
                    <Box
                        mb="22px"
                        sx={{
                            border: "1px solid #E4CBA8",
                            backgroundColor: "var(--tint)",
                            borderRadius: "12px",
                            px: "14px",
                            py: "12px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 10.5,
                                fontWeight: 700,
                                color: "var(--accent-text)",
                                letterSpacing: 0.6,
                                textTransform: "uppercase",
                                fontFamily: "CircularStd",
                                mb: "8px",
                            }}
                        >
                            Check before you submit · {intakeNotes.length}
                        </Typography>
                        <Box component="ul" sx={{ listStyleType: "disc", pl: "18px", m: 0 }}>
                            {intakeNotes.map((note, index) => (
                                <Typography
                                    key={index}
                                    component="li"
                                    sx={{
                                        fontSize: 12.5,
                                        color: "var(--text)",
                                        lineHeight: 1.55,
                                        fontFamily: "CircularStd",
                                        mb: "4px",
                                    }}
                                >
                                    {note}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                )}

                {intakeCreates && (
                    <Box
                        mb="22px"
                        sx={{
                            border: "1px solid var(--border)",
                            backgroundColor: "var(--surface)",
                            borderRadius: "12px",
                            px: "14px",
                            py: "12px",
                        }}
                    >
                        <Box display="flex" justifyContent="space-between" alignItems="center" gap="8px" mb="4px">
                            <Typography
                                sx={{
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    color: "var(--accent-text)",
                                    letterSpacing: 0.6,
                                    textTransform: "uppercase",
                                    fontFamily: "CircularStd",
                                }}
                            >
                                {hasIntakeCreates ? "Created when you submit" : "Brand and products"}
                            </Typography>
                            {onEditIntakeMatches && (
                                <Button
                                    size="small"
                                    onClick={onEditIntakeMatches}
                                    sx={{ textTransform: "none", fontFamily: "CircularStd", fontSize: 12, minWidth: 0, p: "2px 6px" }}
                                >
                                    Change matches
                                </Button>
                            )}
                        </Box>
                        <Typography
                            sx={{ fontSize: 12, color: "var(--text-2)", fontFamily: "CircularStd", mb: hasIntakeCreates ? "8px" : 0 }}
                        >
                            {hasIntakeCreates
                                ? "You confirmed these as new. They are not in the portal yet."
                                : "Everything from the brief is matched to existing brands and products. Nothing new will be created."}
                        </Typography>
                        <Box component="ul" sx={{ listStyleType: "disc", pl: "18px", m: 0 }}>
                            {intakeCreates.brandName && (
                                <Typography
                                    component="li"
                                    sx={{ fontSize: 12.5, color: "var(--text)", lineHeight: 1.55, fontFamily: "CircularStd", mb: "4px" }}
                                >
                                    Supplier brand: <strong>{intakeCreates.brandName}</strong>
                                </Typography>
                            )}
                            {intakeCreates.link && (
                                <Typography
                                    component="li"
                                    sx={{ fontSize: 12.5, color: "var(--text)", lineHeight: 1.55, fontFamily: "CircularStd", mb: "4px" }}
                                >
                                    Retailer link: <strong>{intakeCreates.link}</strong>
                                </Typography>
                            )}
                            {intakeCreates.products.map((product) => (
                                <Typography
                                    key={product.placeholder_id}
                                    component="li"
                                    sx={{ fontSize: 12.5, color: "var(--text)", lineHeight: 1.55, fontFamily: "CircularStd", mb: "4px" }}
                                >
                                    Product: <strong>{product.name}</strong>
                                    {product.pdp_url ? ` · ${product.pdp_url}` : ""}
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Order Details section */}
                <Box mb="22px">
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb="10px">
                        <Typography
                            sx={{
                                fontSize: 10.5,
                                fontWeight: 700,
                                color: "var(--text-2)",
                                letterSpacing: 0.6,
                                textTransform: "uppercase",
                                fontFamily: "CircularStd"
                            }}
                        >
                            Order Details
                        </Typography>
                        <Button
                            size="small"
                            startIcon={<EditIcon sx={{ fontSize: 11 }} />}
                            onClick={onEditOrderDetails}
                            sx={{
                                textTransform: "none",
                                fontSize: 11.5,
                                fontWeight: 600,
                                color: "var(--accent-text)",
                                fontFamily: "CircularStd",
                                p: 0,
                                minWidth: 0,
                                "&:hover": { background: "transparent", textDecoration: "underline" }
                            }}
                        >
                            Edit
                        </Button>
                    </Box>
                    <Box
                        sx={{
                            background: "var(--surface-2)",
                            borderRadius: "12px",
                            padding: "16px 18px"
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <ReviewField label="Order Name" value={values.order_name} />
                            </Grid>
                            <Grid item xs={6}>
                                <ReviewField label="Brand" value={brandName} />
                            </Grid>
                            {!isBrandRole && (
                                <>
                                    <Grid item xs={6}>
                                        <ReviewField label="SOW" value={sowName} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <ReviewField label="Slack Channel" value={slackDisplay} />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Box>
                </Box>

                {/* Expert selection processes — internal-only. Groups projects
                    that share the same experts so casting is done once. */}
                {isInternalRole && (
                    <Box mb="22px">
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb="10px">
                            <Typography
                                sx={{
                                    fontSize: 10.5,
                                    fontWeight: 700,
                                    color: "var(--text-2)",
                                    letterSpacing: 0.6,
                                    textTransform: "uppercase",
                                    fontFamily: "CircularStd"
                                }}
                            >
                                Expert Selection Processes · {orderPros.length}
                            </Typography>
                            <Tooltip title={addProcessTooltip} arrow enterDelay={400}>
                                <span>
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                                        onClick={onAddOrderPro}
                                        disabled={atProcessCapacity}
                                        sx={{
                                            textTransform: "none",
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            color: "var(--accent-text)",
                                            fontFamily: "CircularStd",
                                            p: 0,
                                            minWidth: 0,
                                            "&:hover": { background: "transparent", textDecoration: "underline" },
                                            "&.Mui-disabled": { color: "#C4B896" }
                                        }}
                                    >
                                        Add Process
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                        <Box
                            sx={{
                                background: "var(--surface-2)",
                                borderRadius: "12px",
                                padding: "12px 14px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px"
                            }}
                        >
                            {orderPros.map((op, i) => {
                                const count = projectCountByProKey[op.key] ?? 0;
                                const assigned = count > 0;
                                return (
                                    <Box key={op.key} display="flex" alignItems="center" gap="8px">
                                        <TextField
                                            size="small"
                                            value={op.name}
                                            placeholder={`Selection ${i + 1}`}
                                            onChange={(e) => onRenameOrderPro(op.key, e.target.value)}
                                            sx={{
                                                background: "var(--surface)",
                                                borderRadius: "8px",
                                                width: 168,
                                                flexShrink: 0,
                                                "& .MuiInputBase-input": { fontSize: 13 }
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                color: "var(--accent-text)",
                                                fontFamily: "CircularStd",
                                                whiteSpace: "nowrap",
                                                maxWidth: 150,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                            }}
                                            title={productNameByKey[op.key] || "Any product"}
                                        >
                                            {productNameByKey[op.key] || "Any product"}
                                        </Typography>
                                        {/* Assignment indicator: filled gold dot + count when this
                                            selection holds projects, hollow dot + "Empty" when it
                                            doesn't. Full phrasing on hover. */}
                                        <Tooltip
                                            title={
                                                assigned
                                                    ? `${count} project${count !== 1 ? "s" : ""} on this selection`
                                                    : "No projects assigned yet"
                                            }
                                            arrow
                                            enterDelay={400}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                gap="5px"
                                                sx={{ ml: "auto", flexShrink: 0 }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 6,
                                                        height: 6,
                                                        borderRadius: "50%",
                                                        flexShrink: 0,
                                                        ...(assigned
                                                            ? { background: "var(--accent)" }
                                                            : { border: "1.5px solid var(--muted)" })
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontSize: 10.5,
                                                        fontWeight: 600,
                                                        fontFamily: "CircularStd",
                                                        whiteSpace: "nowrap",
                                                        letterSpacing: 0.2,
                                                        color: assigned ? "var(--accent-text)" : "var(--text-2)"
                                                    }}
                                                >
                                                    {assigned ? count : "Empty"}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                        {orderPros.length > 1 && (
                                            <Tooltip
                                                title={
                                                    op.locked
                                                        ? "Locked — this selection has entered Pre-Production"
                                                        : ""
                                                }
                                                arrow
                                                enterDelay={400}
                                            >
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onRemoveOrderPro(op.key)}
                                                        disabled={!!op.locked}
                                                        sx={{ color: "var(--text-2)" }}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}

                {/* Empty projects warning */}
                {n === 0 && (
                    <Box
                        sx={{
                            p: "12px 16px",
                            background: "var(--tint)",
                            borderRadius: "10px",
                            // amber hairline: no token expresses the warning rule
                            border: "1px solid #F5DFA0",
                            mb: "16px"
                        }}
                    >
                        <Typography sx={{ fontSize: 13, color: "var(--accent-text)", fontFamily: "CircularStd" }}>
                            Add at least one project before submitting.
                        </Typography>
                    </Box>
                )}

                {/* Projects section */}
                <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb="10px">
                        <Typography
                            sx={{
                                fontSize: 10.5,
                                fontWeight: 700,
                                color: "var(--text-2)",
                                letterSpacing: 0.6,
                                textTransform: "uppercase",
                                fontFamily: "CircularStd"
                            }}
                        >
                            Projects · {n}
                        </Typography>
                        <Button
                            size="small"
                            startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                            onClick={onAddProject}
                            sx={{
                                textTransform: "none",
                                fontSize: 11.5,
                                fontWeight: 600,
                                color: "var(--accent-text)",
                                fontFamily: "CircularStd",
                                p: 0,
                                minWidth: 0,
                                "&:hover": { background: "transparent", textDecoration: "underline" }
                            }}
                        >
                            Add Project
                        </Button>
                    </Box>
                    <Box display="flex" flexDirection="column" gap="10px">
                        {projects.map((p, i) => {
                            const currentKey = getProjectProKey(p);
                            const unassigned = isUnassigned(p);
                            const currentProcessLocked = !!orderPros.find((o) => o.key === currentKey)?.locked;
                            const labelFor = (key: string) =>
                                orderPros.find((o) => o.key === key)?.name ||
                                `Selection ${orderPros.findIndex((o) => o.key === key) + 1}`;
                            // Only same-product (or still-empty) processes, plus
                            // the current one so its value stays valid.
                            const chipOptions = showProcessSelector
                                ? orderPros
                                      .filter((op) => {
                                          // Keep the current process selectable;
                                          // never offer a LOCKED process as a
                                          // reassignment target (backend 423s it).
                                          if (op.key === currentKey) return true;
                                          if (op.locked) return false;
                                          return canAssignProject(p, op.key);
                                      })
                                      .map((op) => ({
                                          key: op.key,
                                          label: labelFor(op.key)
                                      }))
                                : [];
                            return (
                                <ProjectSummaryRow
                                    key={p.project_id ?? i}
                                    project={p}
                                    brandId={values.brand?.id}
                                    brand={values.brand}
                                    contentTypes={contentTypes}
                                    projectPrice={pricingByKey?.get(i)?.total ?? null}
                                    priceBreakdown={pricingByKey?.get(i)?.breakdown ?? null}
                                    onEdit={currentProcessLocked ? undefined : () => onEditProject(i)}
                                    onRemove={currentProcessLocked ? undefined : () => onRemoveProject(i)}
                                    error={
                                        unassigned
                                            ? "No selection for this product — use the selection chip to assign or start a new one."
                                            : undefined
                                    }
                                    rightSlot={
                                        showProcessSelector ? (
                                            <ProcessChip
                                                label={unassigned ? "Select…" : labelFor(currentKey)}
                                                options={chipOptions}
                                                currentKey={currentKey}
                                                onSelect={(k) => onAssignProject(i, k)}
                                                onNew={() => onAddOrderProForProject(i)}
                                                canCreateNew={!atProcessCapacity}
                                                readOnly={currentProcessLocked}
                                                unassigned={unassigned}
                                            />
                                        ) : undefined
                                    }
                                />
                            );
                        })}
                    </Box>
                </Box>

                {/* Pricing section — internal-facing quote, hidden from brand requests */}
                {!isBrandRole && pricingTotal != null && (
                    <Box mt="22px">
                        <Typography
                            sx={{
                                fontSize: 10.5,
                                fontWeight: 700,
                                color: "var(--text-2)",
                                letterSpacing: 0.6,
                                textTransform: "uppercase",
                                fontFamily: "CircularStd",
                                mb: "10px"
                            }}
                        >
                            Pricing
                        </Typography>
                        <OrderPricingBreakdown
                            pricingTotal={pricingTotal}
                            pricingBreakdown={pricingBreakdown}
                            pricingSnapshottedAt={pricingSnapshottedAt}
                            layout="summary"
                            // The draft's brand — applies the RMN pricing hide.
                            order={{ brand: values.brand }}
                        />
                    </Box>
                )}
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: { xs: "14px 16px", sm: "14px 28px" },
                    borderTop: "1px solid var(--border)",
                    background: "var(--surface)",
                    flexShrink: 0
                }}
            >
                {onBack ? (
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                        sx={{ textTransform: "capitalize", fontFamily: "CircularStd", color: "var(--text)" }}
                    >
                        Back
                    </Button>
                ) : (
                    <Box />
                )}
                <Box display="flex" alignItems="center" gap="14px">
                    {unassignedCount > 0 ? (
                        <Box display="flex" alignItems="center" gap="6px">
                            <ErrorOutlineRoundedIcon sx={{ fontSize: 15, color: "#EB3E33" }} />
                            <Typography
                                sx={{ fontSize: 12, fontWeight: 600, color: "#C0392B", fontFamily: "CircularStd" }}
                            >
                                {unassignedCount} project{unassignedCount !== 1 ? "s" : ""} need
                                {unassignedCount === 1 ? "s" : ""} a selection
                            </Typography>
                        </Box>
                    ) : (
                        n > 0 && (
                            <Typography sx={{ fontSize: 12, color: "var(--text-2)", fontFamily: "CircularStd" }}>
                                {n} project{n !== 1 ? "s" : ""}
                                {estimatedDays > 0 ? ` · ~${estimatedDays} days to earliest delivery` : ""}
                            </Typography>
                        )
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting || n === 0 || unassignedCount > 0 || !processesReady}
                        onClick={onSubmit}
                        sx={{
                            textTransform: "capitalize",
                            fontFamily: "CircularStd",
                            borderRadius: "24px",
                            px: 3
                        }}
                    >
                        {submitLabel}
                    </Button>
                </Box>
            </Box>
        </>
    );
};
